import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authApi } from '../../services/api';
import '../../components/landing/landing.css';
import toast from 'react-hot-toast';

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();

  // email + initial dev_otp arrive via router state from Register/Login.
  const emailFromState = location.state?.email || '';
  const devOtpFromState = location.state?.devOtp || '';

  const [email]     = useState(emailFromState);
  const [devOtp, setDevOtp] = useState(devOtpFromState);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading]   = useState(false);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);

  // If someone hits /verify-otp directly without an email in state, push them to login.
  useEffect(() => {
    if (!emailFromState) {
      navigate('/login', { replace: true });
    }
  }, [emailFromState, navigate]);

  const code = digits.join('');
  const today = new Date().toISOString().slice(0, 10);

  const handleDigit = (idx, val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 1);
    setDigits(prev => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, 5);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter the full 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authApi.verifyOtp({ email, code });
      localStorage.setItem('calibza_token', data.token);
      localStorage.setItem('calibza_user', JSON.stringify(data.user));
      toast.success('Workspace activated');
      window.location.href = '/app';
    } catch (err) {
      const errs = err.response?.data?.errors;
      if (errs) Object.values(errs).flat().forEach(m => toast.error(m));
      else toast.error(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const { data } = await authApi.resendOtp({ email });
      if (data?.dev_otp) {
        setDevOtp(data.dev_otp);
      }
      toast.success('A fresh code is on its way');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not resend code');
    } finally {
      setResending(false);
    }
  };

  const useDevCode = () => {
    if (!devOtp) return;
    setDigits(devOtp.split('').slice(0, 6));
    inputsRef.current[5]?.focus();
  };

  const copyDevCode = async () => {
    try {
      await navigator.clipboard.writeText(devOtp);
      toast.success('Code copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="cz-root">
      <div className="cz-grain" aria-hidden="true" />

      <div className="cz-auth">
        {/* ── LEFT: certificate panel ────────────────────────────── */}
        <aside className="cz-auth-side">
          <Link to="/" className="cz-wordmark">
            <span className="cz-wordmark-c">C</span>alibZa
            <sup className="cz-wordmark-tm">®</sup>
          </Link>

          <dl className="cz-auth-meta">
            <dt>Document</dt><dd>Auth-04 · Email verification</dd>
            <dt>Issued</dt><dd>{today}</dd>
            <dt>Validity</dt><dd>Code expires in 10 minutes</dd>
          </dl>

          <h1 className="cz-auth-headline">
            Confirm your <em>email.</em>
          </h1>

          <p className="cz-auth-lede">
            We sent a six-digit verification code to{' '}
            <strong style={{ color: 'var(--ink)' }}>{email}</strong>. Enter it
            below to activate your workspace.
          </p>

          <div className="cz-auth-stamp">
            <div className="cz-auth-stamp-head">
              <span>Verification protocol</span>
              <strong>One-time</strong>
            </div>
            <div className="cz-auth-stamp-rows">
              <span className="k">Channel</span><span className="bar" /><span className="v">Signed email · DKIM</span>
              <span className="k">Code length</span><span className="bar" /><span className="v">6 digits</span>
              <span className="k">Expiry</span><span className="bar" /><span className="v">10 minutes</span>
              <span className="k">Retries</span><span className="bar" /><span className="v">Resend on demand</span>
            </div>
          </div>
        </aside>

        {/* ── RIGHT: OTP form ─────────────────────────────────────── */}
        <main className="cz-auth-panel">
          <div className="cz-auth-record">
            <span>Serial № <strong>CZ-OTP-{today}</strong></span>
            <span>Rev. A · Verify</span>
          </div>

          <div className="cz-auth-panel-inner">
            <header className="cz-auth-panel-head">
              <span className="cz-mono-label">§ 01 · Verify</span>
              <h2>Enter your <em>code.</em></h2>
              <p>
                Six digits, sent to{' '}
                <code style={{ fontFamily: 'var(--ff-mono)', fontSize: 13 }}>{email}</code>.
                Paste works too.
              </p>
            </header>

            {devOtp ? (
              <div
                style={{
                  marginBottom: 20,
                  padding: '16px 18px',
                  background: '#fff8d6',
                  border: '1px solid #d9c66a',
                  fontFamily: 'var(--ff-mono)',
                  fontSize: 12,
                  color: '#5b4a00',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  letterSpacing: '0.04em',
                }}
                role="status"
                aria-live="polite"
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  color: '#7a5d00',
                  fontWeight: 600,
                }}>
                  <span>🛠️ Dev mode — email not sent</span>
                </div>
                <button
                  type="button"
                  onClick={copyDevCode}
                  title="Click to copy"
                  style={{
                    appearance: 'none',
                    background: '#fffceb',
                    border: '1px dashed #b89d2a',
                    padding: '12px 16px',
                    fontFamily: 'var(--ff-mono)',
                    fontSize: 22,
                    letterSpacing: '0.5em',
                    color: '#3a2e00',
                    fontWeight: 700,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  {devOtp}
                </button>
                <button
                  type="button"
                  onClick={useDevCode}
                  style={{
                    appearance: 'none',
                    background: '#3a2e00',
                    color: '#fff8d6',
                    border: 'none',
                    padding: '8px 12px',
                    fontFamily: 'var(--ff-mono)',
                    fontSize: 11,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Use this code →
                </button>
                <span style={{ fontSize: 10, color: '#7a5d00' }}>
                  Stage and production environments still email this code normally.
                </span>
              </div>
            ) : null}

            <form className="cz-auth-form" onSubmit={handleSubmit} autoComplete="one-time-code">
              <div className="cz-field">
                <label htmlFor="otp-0" className="cz-field-label">
                  <span className="cz-field-label-num">01</span>
                  <span>Verification code</span>
                </label>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 8,
                  }}
                  onPaste={handlePaste}
                >
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      ref={el => (inputsRef.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      className="cz-input"
                      style={{
                        textAlign: 'center',
                        fontFamily: 'var(--ff-mono)',
                        fontSize: 22,
                        letterSpacing: 0,
                        padding: '14px 0',
                      }}
                      value={d}
                      onChange={e => handleDigit(i, e.target.value)}
                      onKeyDown={e => handleKeyDown(i, e)}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>
              </div>

              <button type="submit" disabled={loading || code.length !== 6} className="cz-auth-submit">
                {loading ? 'Verifying…' : 'Verify and activate'}
                <span className="cz-btn-arrow">→</span>
              </button>
            </form>

            <div className="cz-auth-alt" style={{ justifyContent: 'space-between' }}>
              <span>Didn&rsquo;t get it?</span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                style={{
                  appearance: 'none',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--blue)',
                  fontFamily: 'var(--ff-mono)',
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline',
                }}
              >
                {resending ? 'Sending…' : 'Resend code →'}
              </button>
            </div>

            <p className="cz-auth-legal">
              Wrong email? <Link to="/register">Restart registration →</Link>{' '}
              · <Link to="/login">Back to sign-in</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
